import { useCallback, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import type { TFunction } from 'i18next';

import { EmptyState } from '@/components/common/EmptyState';
import { FeedSkeleton } from '@/components/skeletons/PostCardSkeleton';
import { useToast } from '@/components/common/toast-context';
import type { ShellOutletContext } from '@/components/layout/AppShell';
import { useSession } from '@/features/auth/useSession';
import { FeedFilterBar } from '@/features/feed/FeedFilterBar';
import { FeedToolbar } from '@/features/feed/FeedToolbar';
import { FeedList } from '@/features/feed/FeedList';
import { useFeedFilters } from '@/features/feed/useFeedFilters';
import { useDeletePost, useFeed } from '@/features/feed/useFeed';
import { REGISTERS } from '@/features/posts/registers';
import type { Post } from '@/lib/schemas/post';

export function FeedPage() {
  // The composer dialog lives in the shell, since the nav rail and the mobile
  // FAB open the same one.
  const { onCompose } = useOutletContext<ShellOutletContext>();
  const api = useFeedFilters({ seedFromStorage: true });
  const { filters, setFilters, toggleTag } = api;
  const { isSignedIn } = useSession();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const deletePost = useDeletePost();
  const [deleting, setDeleting] = useState<string | null>(null);

  const feed = useFeed(filters);
  const posts = useMemo(() => feed.data?.pages.flatMap((page) => page.items) ?? [], [feed.data]);

  const handleLoadMore = useCallback(() => {
    void feed.fetchNextPage();
  }, [feed]);

  const handleDelete = (post: Post): void => {
    setDeleting(post.id);
    deletePost.mutate(post.id, {
      onSuccess: () => showToast(t('post.deleted'), 'success'),
      onError: () => showToast(t('post.deleteFailed'), 'error'),
      onSettled: () => setDeleting(null),
    });
  };

  return (
    <Stack spacing={2}>
      {isSignedIn && (
        <Tabs
          value={filters.following ? 1 : 0}
          onChange={(_event, index: number) => setFilters({ following: index === 1 })}
          aria-label={t('nav.home')}
        >
          <Tab label={t('feed.everything')} />
          <Tab label={t('feed.following')} />
        </Tabs>
      )}

      {/* Filters live in the sidebar on wide screens; on narrow ones they are a
          sticky bar that opens a bottom sheet (spec §9.3). */}
      <FeedFilterBar api={api} />

      <FeedToolbar api={api} />

      {feed.isPending ? (
        <FeedSkeleton />
      ) : feed.isError ? (
        <Alert severity="error" role="alert">
          {feed.error.message}
        </Alert>
      ) : posts.length === 0 ? (
        <Card>
          <EmptyState
            {...emptyStateFor(t, filters, isSignedIn)}
            action={
              isSignedIn ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={onCompose}>
                  {t('feed.writeOne')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <Box sx={{ opacity: deleting ? 0.7 : 1, transition: 'opacity 150ms' }}>
          <FeedList
            posts={posts}
            hasNextPage={feed.hasNextPage}
            isFetchingNextPage={feed.isFetchingNextPage}
            onLoadMore={handleLoadMore}
            onTagClick={toggleTag}
            onDelete={handleDelete}
          />
        </Box>
      )}
    </Stack>
  );
}

/**
 * Empty states name the actual filter that produced them (spec §9.3) — "no bad
 * posts in Timișoara this week" says something; "no results" does not.
 */
function emptyStateFor(
  t: TFunction,
  filters: ReturnType<typeof useFeedFilters>['filters'],
  isSignedIn: boolean,
): { title: string; description: string; accent?: 'good' | 'bad' | 'ideal' } {
  if (filters.following) {
    return {
      title: t('feed.empty.following.title'),
      description: isSignedIn
        ? t('feed.empty.following.signedIn')
        : t('feed.empty.following.signedOut'),
    };
  }

  const place = filters.town || countryLabel(filters.country);
  const single = filters.types.length === 1 ? filters.types[0] : null;

  if (single && place) {
    const register = t(`registers.${single}.label`).toLocaleLowerCase();
    return {
      title: t('feed.empty.registerInPlace.title', { register, place }),
      description:
        single === 'BAD'
          ? t('feed.empty.registerInPlace.badDescription')
          : t('feed.empty.registerInPlace.description'),
      accent: REGISTERS[single].accent,
    };
  }

  if (place) {
    return {
      title: t('feed.empty.place.title', { place }),
      description: t('feed.empty.place.description'),
    };
  }

  if (single) {
    const register = t(`registers.${single}.label`).toLocaleLowerCase();
    return {
      title: t('feed.empty.register.title', { register }),
      description: t('feed.empty.register.description'),
      accent: REGISTERS[single].accent,
    };
  }

  return {
    title: t('feed.empty.filters.title'),
    description: t('feed.empty.filters.description'),
  };
}

function countryLabel(code: string): string {
  return code || '';
}
